import React, { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

interface LocationFAQProps {
  locationName: string;
  locationData: {
    bundesland?: string;
    kreis?: string;
    strasse?: string;
    postleitzahl?: string;
    ort?: string;
  };
  className?: string;
}

const LocationFAQ: React.FC<LocationFAQProps> = ({ locationName, locationData, className = '' }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const generateFAQs = (): FAQItem[] => {
    const faqs: FAQItem[] = [
      {
        question: `Welche Dienstleistungen bietet das Einwohnermeldeamt ${locationName}?`,
        answer: `Das Einwohnermeldeamt ${locationName} ist zuständig für alle melderechtlichen Angelegenheiten. Zu den wichtigsten Dienstleistungen gehören: An- und Abmeldung bei Umzügen, Ausstellung von Meldebescheinigungen, Melderegisterauskünfte, Beantragung und Verlängerung von Personalausweisen und Reisepässen sowie die Ausstellung von Führungszeugnissen.`
      },
      {
        question: `Wie kann ich eine Melderegisterauskunft ${locationName} beantragen?`,
        answer: `Eine Melderegisterauskunft können Sie entweder persönlich beim Einwohnermeldeamt ${locationName}, schriftlich per Post oder bequem online über AdressFinder.org beantragen. Die Online-Beantragung ist 24/7 verfügbar und spart Ihnen den Behördengang.`
      },
      {
        question: `Wo befindet sich das Einwohnermeldeamt ${locationName}?`,
        answer: locationData.strasse && locationData.ort
          ? `Das Einwohnermeldeamt ${locationName} befindet sich hier: ${locationData.strasse}, ${locationData.ort}${locationData.bundesland ? `, ${locationData.bundesland}` : ''}.`
          : `Das Einwohnermeldeamt ${locationName} ist für alle melderechtlichen Angelegenheiten der Bürgerinnen und Bürger zuständig. Die genaue Adresse und Kontaktdaten finden Sie auf dieser Seite oben.`
      },
      {
        question: `Kann ich beim Einwohnermeldeamt ${locationName} auch online Termine vereinbaren?`,
        answer: `Informationen zur Terminvereinbarung erhalten Sie direkt beim Einwohnermeldeamt ${locationName}. Viele Ämter bieten mittlerweile Online-Terminbuchungen an. Für Melderegisterauskünfte können Sie AdressFinder.org nutzen – ohne Terminvereinbarung, 24/7 verfügbar.`
      },
      {
        question: `Welche Unterlagen benötige ich für eine An- oder Ummeldung in ${locationName}?`,
        answer: `Für eine An- oder Ummeldung beim Einwohnermeldeamt ${locationName} benötigen Sie in der Regel: einen gültigen Personalausweis oder Reisepass, die Wohnungsgeberbestätigung (vom Vermieter ausgefüllt) sowie ggf. weitere Dokumente wie Heiratsurkunde oder Geburtsurkunden der Kinder. Die Anmeldung muss innerhalb von 14 Tagen nach dem Einzug erfolgen.`
      },
      {
        question: `Bietet das Einwohnermeldeamt ${locationName} auch Online-Services an?`,
        answer: `Viele Meldeämter in Deutschland erweitern zunehmend ihre Online-Angebote. Ob das Einwohnermeldeamt ${locationName} Online-Services anbietet, erfahren Sie auf der offiziellen Webseite der Gemeinde. Unabhängig davon können Sie Melderegisterauskünfte bequem online über AdressFinder.org beantragen – ohne Behördenbesuch, schnell und unkompliziert.`
      }
    ];

    return faqs;
  };

  const faqs = generateFAQs();

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className={className}>
      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden"
          >
            <button
              onClick={() => toggleFAQ(index)}
              className="w-full text-left px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              aria-expanded={openIndex === index}
            >
              <h3 className="text-lg font-semibold pr-8" style={{ color: '#023047' }}>
                {faq.question}
              </h3>
              <svg
                className={`w-6 h-6 flex-shrink-0 transform transition-transform duration-200 ${
                  openIndex === index ? 'rotate-180' : ''
                }`}
                style={{ color: '#219ebc' }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            
            <div
              className={`transition-all duration-300 ease-in-out ${
                openIndex === index
                  ? 'max-h-96 opacity-100'
                  : 'max-h-0 opacity-0'
              } overflow-hidden`}
            >
              <div className="px-6 pb-4 text-gray-700 leading-relaxed">
                {faq.answer}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LocationFAQ;

